// Reusable error display components for various error scenarios

import React from 'react';
import { type AppError } from '../utils/errorHandling';

interface ErrorDisplayProps {
  error: AppError;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'banner' | 'card' | 'inline' | 'toast';
  showDetails?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  onDismiss,
  className = '',
  variant = 'card',
  showDetails = false,
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'banner':
        return 'w-full p-4 border-l-4 border-red-400 bg-red-500/10';
      case 'card':
        return 'max-w-md mx-auto bg-navy-800 shadow-lg rounded-lg p-6 border border-red-500/40';
      case 'inline':
        return 'p-3 bg-red-500/10 border border-red-500/40 rounded-md';
      case 'toast':
        return 'fixed top-4 right-4 max-w-sm bg-navy-800 shadow-lg rounded-lg p-4 border border-red-500/40 z-50';
      default:
        return 'p-4 bg-red-500/10 border border-red-500/40 rounded-md';
    }
  };

  const getIconColor = () => {
    switch (error.type) {
      case 'network':
        return 'text-orange-500';
      case 'validation':
        return 'text-yellow-500';
      case 'timing':
        return 'text-gold-400';
      case 'data':
        return 'text-gold-400';
      case 'auth':
        return 'text-red-400';
      case 'session':
        return 'text-gold-400';
      case 'permission':
        return 'text-red-400';
      case 'not_found':
        return 'text-navy-300';
      case 'server':
        return 'text-red-400';
      default:
        return 'text-red-500';
    }
  };

  const getIcon = () => {
    switch (error.type) {
      case 'network':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
        );
      case 'validation':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'timing':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'data':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        );
      case 'auth':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      case 'session':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'permission':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        );
      case 'not_found':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'server':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
    }
  };

  return (
    <div className={`${getVariantClasses()} ${className}`} role="alert">
      <div className="flex">
        <div className={`flex-shrink-0 ${getIconColor()}`}>
          {getIcon()}
        </div>
        
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-white">
            {error.type === 'network' && 'Connection Problem'}
            {error.type === 'validation' && 'Validation Error'}
            {error.type === 'timing' && 'Timing Issue'}
            {error.type === 'data' && 'Data Error'}
            {error.type === 'auth' && 'Authentication Error'}
            {error.type === 'session' && 'Session Error'}
            {error.type === 'permission' && 'Permission Denied'}
            {error.type === 'not_found' && 'Not Found'}
            {error.type === 'server' && 'Server Error'}
            {error.type === 'unknown' && 'Unexpected Error'}
          </h3>
          
          <div className="mt-1 text-sm text-navy-100">
            {error.userMessage || error.message || 'An error occurred. Please try again.'}
          </div>
          
          {showDetails && error.code && (
            <div className="mt-2 text-xs text-navy-300">
              Error Code: {error.code}
            </div>
          )}
          
          {(onRetry || onDismiss) && (
            <div className="mt-3 flex space-x-2">
              {onRetry && error.retryable && (
                <button
                  onClick={onRetry}
                  className="text-sm bg-red-500/15 text-red-400 hover:bg-red-500/20 px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Try Again
                </button>
              )}
              
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="text-sm text-navy-200 hover:text-white px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-600"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
        
        {onDismiss && variant === 'toast' && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onDismiss}
              className="inline-flex text-navy-300 hover:text-navy-200 focus:outline-none focus:ring-2 focus:ring-navy-600"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Network status indicator component
interface NetworkStatusProps {
  isOnline: boolean;
  className?: string;
}

export const NetworkStatus: React.FC<NetworkStatusProps> = ({ isOnline, className = '' }) => {
  if (isOnline) return null;

  return (
    <div className={`bg-yellow-500/10 border-l-4 border-yellow-400 p-4 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-400">
            You're currently offline. Some features may not be available.
          </p>
        </div>
      </div>
    </div>
  );
};

// Loading state with error fallback
interface LoadingWithErrorProps {
  isLoading: boolean;
  error: AppError | null;
  onRetry?: () => void;
  children: React.ReactNode;
  loadingText?: string;
}

export const LoadingWithError: React.FC<LoadingWithErrorProps> = ({
  isLoading,
  error,
  onRetry,
  children,
  loadingText = 'Loading...',
}) => {
  if (error) {
    return (
      <ErrorDisplay 
        error={error} 
        onRetry={onRetry}
        variant="card"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500"></div>
        <span className="ml-3 text-navy-200">{loadingText}</span>
      </div>
    );
  }

  return <>{children}</>;
};

// Validation error list component
interface ValidationErrorListProps {
  errors: Record<string, string>;
  className?: string;
}

export const ValidationErrorList: React.FC<ValidationErrorListProps> = ({
  errors,
  className = '',
}) => {
  const errorEntries = Object.entries(errors);
  
  if (errorEntries.length === 0) return null;

  return (
    <div className={`bg-red-500/10 border border-red-500/40 rounded-md p-4 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-400">
            Please correct the following errors:
          </h3>
          <div className="mt-2 text-sm text-red-400">
            <ul className="list-disc pl-5 space-y-1">
              {errorEntries.map(([field, message]) => (
                <li key={field}>
                  <span className="font-medium capitalize">{field}:</span> {message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;